import { IQuizLoader } from './i-quiz-loader';
import { Language } from '../../dto/language';
import { QuizStep, QuizMode } from '../../dto/user-words';
import { MakeReactive, noopMakeReactive } from '../make-reactive';
import { IQuizzService } from '../../services/i-quizz-service';

interface QuizLoaderState {
	steps: QuizStep[];
	isLoading: boolean;
	error: string;
}

/**
 * Framework-agnostic quiz loader.
 * Manages loading quiz questions and options.
 */
export class QuizLoader extends IQuizLoader {
	private readonly state: QuizLoaderState;

	constructor(
		service: IQuizzService,
		userId: string | undefined,
		nativeLanguage: Language,
		learnedLanguage: Language,
		mode: QuizMode,
		articleTranslationId?: string,
		maxSteps?: number,
		makeReactive: MakeReactive<unknown> = noopMakeReactive
	) {
		super(service, userId, nativeLanguage, learnedLanguage, mode, articleTranslationId, maxSteps, makeReactive);

		this.state = makeReactive({
			steps: [],
			isLoading: false,
			error: '',
		}) as QuizLoaderState;
	}

	isLoading(): boolean {
		return this.state.isLoading;
	}

	get steps(): QuizStep[] {
		return this.state.steps;
	}

	get error(): string {
		return this.state.error;
	}

	async loadQuiz(): Promise<void> {
		if (this.state.isLoading) return;

		try {
			this.state.isLoading = true;
			this.state.error = '';

			const response = await this.service.getQuizOptions({
				userId: this.userId,
				nativeLanguage: this.nativeLanguage,
				learnedLanguage: this.learnedLanguage,
				mode: this.mode,
				articleTranslationId: this.articleTranslationId,
				maxSteps: this.maxSteps,
			});

			this.state.steps = response.steps;
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error loading quiz';
			this.state.error = errorMessage;
			// eslint-disable-next-line no-console
			console.error('Error loading quiz:', error);
		} finally {
			this.state.isLoading = false;
		}
	}

	clearCache(): void {
		this.state.steps = [];
		this.state.isLoading = false;
		this.state.error = '';
	}
}

